import { useCallback, useEffect, useRef } from 'react';
import { phoenixAccountGetCustomerWishlists } from './queries';
import { useLazyQuery } from '@apollo/client';
import { useWishListDetailsGlobalStore, transformUxlToStoreWishlist } from './wishListDetailsGlobalStore';
import { WishListCustomerWishlistType } from './wishListDetailsGlobalStore.types';

interface UseWishListDetailsFetchOptions {
  customerId?: string;
  skip?: boolean;
  context?: Record<string, unknown>;
}

export const useWishListDetailsFetch = ({ customerId, skip, context }: UseWishListDetailsFetchOptions) => {
  const { setWishListDetailsInfo, wishlist, hasFetchedWishListDetails, isWishListDetailsSaved } =
    useWishListDetailsGlobalStore();

  const contextRef = useRef(context);
  contextRef.current = context;

  const [executeQuery, { loading, error }] = useLazyQuery<{
    customerWishlist?: WishListCustomerWishlistType;
  }>(phoenixAccountGetCustomerWishlists, {
    fetchPolicy: 'no-cache',
    onCompleted: responseData => {
      const rawWishlists = responseData?.customerWishlist?.wishlists ?? [];
      const transformedWishlist = transformUxlToStoreWishlist(rawWishlists);
      setWishListDetailsInfo({
        wishlist: transformedWishlist,
        hasFetchedWishListDetails: true,
      });
    },
    onError: err => {
      setWishListDetailsInfo({
        wishlist: [],
        hasFetchedWishListDetails: true,
        error: err,
      });
    },
  });

  const triggerFetch = useCallback(() => {
    if (customerId) {
      executeQuery({
        variables: { customerId },
        context: contextRef.current,
      });
    }
  }, [customerId, executeQuery]);

  useEffect(() => {
    if (!skip && !hasFetchedWishListDetails && customerId) {
      triggerFetch();
    }
  }, [skip, hasFetchedWishListDetails, customerId, triggerFetch]);

  return { loading, error, triggerFetch, wishlist, hasFetchedWishListDetails, isWishListDetailsSaved };
};
